import { BillingHoldEntry, DBMetadata } from '../../../types'
import { ProviderAuthentication } from '../../../types/providers'
import { EncounterData } from './encounter'
import { EncounterType } from './encounterType'
import { RiskAssessment } from './shared'

export enum EncounterStatus {
  DRAFT = 'draft',
  COMPLETED = 'completed',
  AUTHENTICATED = 'authenticated',
}

export interface BaseEncounter<Type extends EncounterType> extends DBMetadata {
  type: Type
  practiceId: string
  patientId: string
  assessmentId?: string | null
  claimIds: Array<string>
  status: EncounterStatus
  renderingProvider: string | null
  supervisingProvider: string | null
  attendingProvider: string | null
  renderingProviderInitials: string | null
  supervisingProviderInitials: string | null
  attendingProviderInitials: string | null
  date?: string | null
  riskAssessment: RiskAssessment
  authentication?: ProviderAuthentication<EncounterData<Type>>
  // §7.5 QA reporting: denormalized top-level arrays for Firestore composite index queries.
  // Populated by saveEncounter when ICD-10 and CPT codes are available.
  // Required before marketing Mode 2 birthing center system (NYS Article 28).
  icd10Codes?: string[]
  cptCodes?: string[]
  // §2.11: Amendment trail — each post-authentication edit appends an entry here.
  amendments?: Array<{ on: number; by: string; reason: string }>
  // §6.2: Timestamp set when billing team opens and reviews this encounter's claim.
  billingReviewedOn?: number
  // §6.4.2: Timestamp set when provider acknowledges the critical notice (Day 52).
  criticalNoticeAcknowledgedOn?: number
  // §6.4.2: Manual outreach log for Day 59+ billing alerts.
  outreachLog?: Array<{ on: number; by: string; outcome: string }>
  // §6.4.2: Timestamp set when billing team submits a claim under draft authorization (encounter still COMPLETED).
  draftAuthorizationSubmittedOn?: number
  // §A: Author identity captured at first save of a new note — immutable after initial write.
  noteAuthor?: {
    userId: string
    displayName: string
    initials: string
    capturedAt: number
  } | null
  // §A: Provider who performed the procedure (deliveries, lacerations, resuscitations, POCUS).
  // Mutable on drafts, locked once the encounter is authenticated.
  performingProvider?: string | null
  // §I Gap 1: Set to true when a patient submits a written statement for this encounter.
  hasPatientStatement?: boolean
  // §1.6.2 Hold from Billing — post-authentication, provider-only. Active hold (null when
  // released) plus append-only history; written exclusively by placeEncounterBillingHold.
  currentBillingHold?: BillingHoldEntry | null
  billingHoldHistory?: BillingHoldEntry[]
}
